import { XMarkIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'

import { TextInput } from '@/components/forms/TextInput'
import { SearchIcon } from '@/components/icons'
import { DataTable, getSortProps } from '@/components/shared/DataTable'
import { FilterPopover } from '@/components/shared/FilterPopover'
import useAbortControllers from '@/hooks/AbortControllers'
import { getRepairOrders } from '@/services/dealer.service'
import { getDealers, getFilterListOptions } from '@/services/dealers.service'
import { GlobalSettingsContext } from '@/stores/GlobalSettingsStore'
import { handlePagination } from '@/utils/helpers'

import { COLUMNS, OL_STATE_OPTIONS, STATUS_OPTIONS } from './constants'

// `handlePagination` builds page URLs as `${RO_BASE_URL}${perPage}&...`, so the
// base must end with `limit=`.
const RO_BASE_URL = '/repair-orders/?limit='
const DEFAULT_SORT = '-created_at'

const DEFAULT_FILTERS = {
  status: [],
  state: [],
  dealer: [],
  group: [],
  admin: [],
  manager: [],
  assistantManager: [],
}

// Maps each selectedFilters key to its backend query param. The dealer-contact
// filters mirror the Dealer List and are applied through the RO's dealer.
const FILTER_PARAMS = {
  status: 'status',
  state: 'state',
  dealer: 'dealer',
  group: 'group',
  admin: 'admin',
  manager: 'manager',
  assistantManager: 'assistant_manager',
}

// Date-range filters. Keys are the backend query params (date-only, inclusive
// on both ends). Empty string means "no bound".
const DEFAULT_DATE_FILTERS = {
  received_at__gte: '',
  received_at__lte: '',
  last_updated_on__gte: '',
  last_updated_on__lte: '',
}

// The two date-range groups rendered in the filter bar.
const DATE_RANGES = [
  { label: 'Received', from: 'received_at__gte', to: 'received_at__lte' },
  { label: 'Last Updated', from: 'last_updated_on__gte', to: 'last_updated_on__lte' },
]

// Display labels for the active-filter summary chips.
const FILTER_LABELS = {
  status: 'Status',
  state: 'OL Status',
  dealer: 'Dealer',
  group: 'Dealer Group',
  admin: 'Admin',
  manager: 'Manager',
  assistantManager: 'Assistant Manager',
}

// Human summary of a from/to date range for a chip.
const dateRangeSummary = (from, to) => {
  if (from && to) return `${dayjs(from).format('MMM D')} – ${dayjs(to).format('MMM D, YYYY')}`
  if (from) return `From ${dayjs(from).format('MMM D, YYYY')}`
  return `Until ${dayjs(to).format('MMM D, YYYY')}`
}

// Normalize an API options response into the [{ id, label }] MultiSelectDropdown
// shape: objects use { id, name }, primitives (name strings) use the value itself.
const toOptions = (values, isObject = false) =>
  _.orderBy(
    _.map(values || [], (option) =>
      isObject ? { id: option.id, label: option.name } : { id: option, label: option },
    ),
    'label',
  )

/**
 *
 * RepairOrders
 *
 * Global, cross-dealer repair order list. Shows every RO the signed-in user can
 * access (scoping is enforced server-side). Read-only — rows link to the
 * existing dealer-scoped RO detail page.
 */
const RepairOrders = observer(() => {
  const navigate = useNavigate()
  const { getSignal } = useAbortControllers()
  const searchInputRef = useRef(null)
  const { isGlobalRoListEnabled, loaded: settingsLoaded } = useContext(GlobalSettingsContext)

  // Guard direct navigation when the feature flag is off (mirrors RO Intake).
  useEffect(() => {
    if (settingsLoaded && !isGlobalRoListEnabled) {
      navigate('/dashboard', { replace: true })
    }
  }, [settingsLoaded, isGlobalRoListEnabled])

  const [repairOrders, setRepairOrders] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [pages, setPages] = useState({ next: null, previous: null })
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [sortedColumn, setSortedColumn] = useState(DEFAULT_SORT)
  const [searchTerm, setSearchTerm] = useState('')
  const [search, setSearch] = useState(null)
  const [selectedFilters, setSelectedFilters] = useState(DEFAULT_FILTERS)
  const [dateFilters, setDateFilters] = useState(DEFAULT_DATE_FILTERS)
  const [filterOptions, setFilterOptions] = useState({
    dealer: [],
    group: [],
    admin: [],
    manager: [],
    assistantManager: [],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const searchRepairOrders = useCallback(_.debounce(setSearch, 500), [])

  const filterQuery = useMemo(() => {
    const parts = [`order_by=${sortedColumn}`]
    if (search) parts.push(`q=${encodeURIComponent(search)}`)
    Object.entries(FILTER_PARAMS).forEach(([key, param]) => {
      const values = selectedFilters[key]
      if (values && values.length) {
        parts.push(values.map((value) => `${param}=${encodeURIComponent(value.id)}`).join('&'))
      }
    })
    Object.entries(dateFilters).forEach(([param, value]) => {
      if (value) parts.push(`${param}=${encodeURIComponent(value)}`)
    })
    return parts.join('&')
  }, [sortedColumn, search, selectedFilters, dateFilters])

  // Active filters summarized as removable chips, so the current scope is
  // visible even when the filter panel is closed.
  const activeChips = useMemo(() => {
    const chips = []
    Object.entries(selectedFilters).forEach(([key, values]) => {
      values.forEach((value) => {
        chips.push({
          id: `${key}:${value.id}`,
          text: `${FILTER_LABELS[key]}: ${value.label}`,
          remove: () =>
            setSelectedFilters((prev) => ({
              ...prev,
              [key]: prev[key].filter((v) => v.id !== value.id),
            })),
        })
      })
    })
    DATE_RANGES.forEach(({ label, from, to }) => {
      if (dateFilters[from] || dateFilters[to]) {
        chips.push({
          id: from,
          text: `${label}: ${dateRangeSummary(dateFilters[from], dateFilters[to])}`,
          remove: () => setDateFilters((prev) => ({ ...prev, [from]: '', [to]: '' })),
        })
      }
    })
    return chips
  }, [selectedFilters, dateFilters])

  // Load the dropdown options once. Each endpoint is already scoped to the
  // dealers the user can access (same scoping the Dealer List uses).
  useEffect(() => {
    // Don't fetch until we know the feature is enabled for this user.
    if (!settingsLoaded || !isGlobalRoListEnabled) return undefined
    let active = true
    const loadOptions = async () => {
      const [groups, admins, managers, assistants, dealersResponse] = await Promise.all([
        getFilterListOptions(
          '/dealers/dealer-groups/',
          () => {},
          () => {},
          getSignal('opt-groups'),
        ),
        getFilterListOptions(
          '/dealers/admins/',
          () => {},
          () => {},
          getSignal('opt-admins'),
        ),
        getFilterListOptions(
          '/dealers/managers/',
          () => {},
          () => {},
          getSignal('opt-managers'),
        ),
        getFilterListOptions(
          '/dealers/assistant-managers/',
          () => {},
          () => {},
          getSignal('opt-assistants'),
        ),
        // High limit so the dealer dropdown is effectively complete for most
        // users; server-side dealer search is a follow-up if scale demands it.
        getDealers(
          '/dealers/?limit=1000',
          () => {},
          () => {},
          getSignal('opt-dealers'),
        ),
      ])

      if (!active) return
      setFilterOptions({
        group: toOptions(groups, true),
        admin: toOptions(admins),
        manager: toOptions(managers),
        assistantManager: toOptions(assistants),
        dealer: toOptions(dealersResponse?.results, true),
      })
    }

    loadOptions()
    return () => {
      active = false
    }
  }, [settingsLoaded, isGlobalRoListEnabled])

  const loadPage = async (url) => {
    const response = await getRepairOrders(
      url,
      setLoading,
      setError,
      () => {},
      getSignal('global-repair-orders'),
    )

    if (response) {
      setRepairOrders(response.results)
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
    }

    return response
  }

  // Reset to the first page whenever the filters, sort, or page size change.
  useEffect(() => {
    // Don't fetch until we know the feature is enabled for this user.
    if (!settingsLoaded || !isGlobalRoListEnabled) return
    const loadFirstPage = async () => {
      const response = await loadPage(`${RO_BASE_URL}${perPage}&${filterQuery}`)
      if (response) setCurrentPage(1)
    }

    loadFirstPage()
  }, [filterQuery, perPage, settingsLoaded, isGlobalRoListEnabled])

  // Dropdowns rendered inside the filter panel. `search` on the longer,
  // dealer-derived lists; Status/OL Status are short enough without it.
  const dropdownFilters = [
    { key: 'status', label: 'Status', id: 'status-filter' },
    { key: 'state', label: 'OL Status', id: 'state-filter' },
    { key: 'dealer', label: 'Dealer', id: 'dealer-filter', search: true },
    { key: 'group', label: 'Dealer Group', id: 'group-filter', search: true },
    { key: 'admin', label: 'Admin', id: 'admin-filter', search: true },
    { key: 'manager', label: 'Manager', id: 'manager-filter', search: true },
    {
      key: 'assistantManager',
      label: 'Assistant Manager',
      id: 'assistant-manager-filter',
      search: true,
    },
  ]

  const dropdownOptions = {
    status: STATUS_OPTIONS,
    state: OL_STATE_OPTIONS,
    dealer: filterOptions.dealer,
    group: filterOptions.group,
    admin: filterOptions.admin,
    manager: filterOptions.manager,
    assistantManager: filterOptions.assistantManager,
  }

  return (
    <div className="flex flex-col">
      <Helmet>
        <title>Repair Orders</title>
      </Helmet>

      <h5 className="mb-0">Repair Orders</h5>
      <p className="text-charcoal-400 text-sm">
        Every repair order across the dealers you have access to.
      </p>

      <div className="mb-4 flex flex-col">
        <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
          <TextInput
            className="w-full pl-10 md:w-80"
            containerClassName="w-full md:w-80"
            data-testid="search"
            icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
            endIcon={
              searchTerm ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('')
                    searchRepairOrders(null)
                    if (searchInputRef.current) searchInputRef.current.value = ''
                  }}
                >
                  <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                </button>
              ) : null
            }
            onChange={(e) => {
              setSearchTerm(e.target.value)
              searchRepairOrders(e.target.value)
            }}
            placeholder="Search RO #, advisor, or technician"
            value={searchTerm}
            ref={searchInputRef}
          />

          <FilterPopover
            filters={selectedFilters}
            onFiltersChange={setSelectedFilters}
            checkboxFilters={[]}
            dropdownFilters={dropdownFilters}
            dropdownOptions={dropdownOptions}
            dropdownColumns={2}
            defaultFilters={DEFAULT_FILTERS}
            dateRangeFilters={DATE_RANGES}
            dateValues={dateFilters}
            onDateChange={setDateFilters}
            anchor="bottom end"
            ariaLabel="Filter repair orders"
          />
        </div>

        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pb-3">
            {activeChips.map((chip) => (
              <span
                key={chip.id}
                className="bg-brownGray-100 text-charcoal-900 inline-flex items-center gap-1 rounded-full py-1 pl-3 pr-2 text-xs font-medium"
              >
                {chip.text}
                <button
                  type="button"
                  onClick={chip.remove}
                  aria-label={`Remove ${chip.text} filter`}
                  className="text-charcoal-400 hover:text-charcoal-900"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => {
                setSelectedFilters(DEFAULT_FILTERS)
                setDateFilters(DEFAULT_DATE_FILTERS)
              }}
              className="text-xs font-semibold text-blue-800 hover:text-blue-900"
            >
              Clear all
            </button>
          </div>
        )}

        {error && <p className="text-red py-2 text-sm font-semibold">{error}</p>}

        <DataTable
          columns={COLUMNS}
          data={repairOrders}
          highlightOnHover
          onRowClicked={(row) => navigate(`/dealer/${row.dealer?.id}/repair-order/${row.id}`)}
          {...getSortProps(sortedColumn, COLUMNS)}
          onChangePage={(page) =>
            handlePagination(
              page,
              currentPage,
              perPage,
              totalRows,
              pages,
              setCurrentPage,
              loadPage,
              RO_BASE_URL,
              filterQuery,
            )
          }
          onChangeRowsPerPage={(rows) => setPerPage(rows)}
          onSort={(column, direction) => {
            const prefix = direction === 'asc' ? '' : '-'
            setSortedColumn(`${prefix}${column.sortBy}`)
          }}
          pagination
          paginationServer
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          paginationTotalRows={totalRows}
          progressPending={loading}
          sortServer
          noDataComponent={<div className="text-charcoal-400 py-10">No repair orders found.</div>}
        />
      </div>
    </div>
  )
})

export default RepairOrders
